import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Route, Routes, useLocation } from "react-router-dom";

import { useIsMobile } from "@ryde/shared";

import { HelpTopicsMobileToolbar } from "./help-topics-mobile-toolbar";
import { HelpTopicsSidebar } from "./help-topics-sidebar";
import { HelpTopicsTopicView } from "./help-topics-topic.view";
import {
  HelpTopicsContentInnerStyled,
  HelpTopicsContentStyled,
  HelpTopicsPageStyled,
  HelpTopicsSidebarBackdropStyled,
  HelpTopicsWelcomeStyled,
} from "./help-topics.page.styled";

const HelpTopicsWelcome: FC = () => {
  const { t } = useTranslation();

  return (
    <HelpTopicsWelcomeStyled>
      <h2>
        {t("helpTopics.welcomeTitle", { defaultValue: "Ryde help center" })}
      </h2>
      <p>
        {t("helpTopics.welcomeBody", {
          defaultValue:
            "Browse topics in the sidebar or click the blue help icon next to a system parameter field to open contextual documentation without leaving your form.",
        })}
      </p>
      <p>
        {t("helpTopics.welcomeHint", {
          defaultValue:
            "Production system parameters are documented first. More groups will appear here as documentation grows.",
        })}
      </p>
    </HelpTopicsWelcomeStyled>
  );
};

export const HelpTopicsPage: FC = () => {
  const location = useLocation();
  const { isMobile } = useIsMobile();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const basePath = useMemo(() => {
    const helptopicsIndex = location.pathname.indexOf("/helptopics");
    if (helptopicsIndex === -1) {
      return "/helptopics";
    }
    return location.pathname.slice(0, helptopicsIndex + "/helptopics".length);
  }, [location.pathname]);

  const closeSidebar = useCallback(() => {
    setIsSidebarOpen(false);
  }, []);

  const openSidebar = useCallback(() => {
    setIsSidebarOpen(true);
  }, []);

  useEffect(() => {
    closeSidebar();
  }, [location.pathname, closeSidebar]);

  useEffect(() => {
    if (!isMobile) {
      closeSidebar();
    }
  }, [isMobile, closeSidebar]);

  return (
    <HelpTopicsPageStyled>
      {isMobile && (
        <HelpTopicsSidebarBackdropStyled
          $visible={isSidebarOpen}
          onClick={closeSidebar}
          tabIndex={isSidebarOpen ? 0 : -1}
        />
      )}
      <HelpTopicsSidebar
        basePath={basePath}
        isMobileOpen={isMobile && isSidebarOpen}
        onNavigate={isMobile ? closeSidebar : undefined}
      />
      <HelpTopicsContentStyled>
        {isMobile && <HelpTopicsMobileToolbar onOpenSidebar={openSidebar} />}
        <HelpTopicsContentInnerStyled>
          <Routes>
            <Route index element={<HelpTopicsWelcome />} />
            <Route path="*" element={<HelpTopicsTopicView />} />
          </Routes>
        </HelpTopicsContentInnerStyled>
      </HelpTopicsContentStyled>
    </HelpTopicsPageStyled>
  );
};
